import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import { useMapAnalytics } from '../../hooks/useMapAnalytics'

/**
 * MapZoomTracker - Tracks zoom events on the map
 * 
 * This component listens to Leaflet's zoomend event and tracks zoom changes
 * with their trigger source (user interaction, programmatic, etc.)
 * 
 * Tracks:
 * - Zoom level changes
 * - Map center after zoom
 * - Trigger source (zoom buttons, mouse wheel, double-click, pinch, programmatic)
 */
export function MapZoomTracker() {
  const map = useMap()
  const { trackMapZoom } = useMapAnalytics()
  const lastZoom = useRef(map.getZoom())
  const zoomStartSource = useRef(null)

  useEffect(() => {
    if (!map) return

    // Track zoom start to determine source
    const handleZoomStart = (e) => {
      // Check if zoom was triggered by user interaction or programmatically
      const originalEvent = e.originalEvent
      
      if (!originalEvent) {
        zoomStartSource.current = 'programmatic'
      } else if (originalEvent.type === 'dblclick') {
        zoomStartSource.current = 'double_click'
      } else if (originalEvent.type === 'wheel') {
        zoomStartSource.current = 'mouse_wheel'
      } else if (originalEvent.type === 'touchstart' || originalEvent.type === 'touchmove') {
        zoomStartSource.current = 'pinch_zoom'
      } else if (originalEvent.type === 'click') {
        // Likely from zoom control buttons
        zoomStartSource.current = 'zoom_control_button'
      } else {
        zoomStartSource.current = 'user_interaction'
      }
    }

    // Track zoom end with final zoom level
    const handleZoomEnd = () => {
      const newZoom = map.getZoom()
      const oldZoom = lastZoom.current
      
      // Only track if zoom actually changed
      if (newZoom !== oldZoom) {
        const trigger = zoomStartSource.current || 'unknown'
        
        trackMapZoom(newZoom, trigger)
        
        lastZoom.current = newZoom
      }
      
      // Reset source
      zoomStartSource.current = null
    }

    // Listen to zoom events
    map.on('zoomstart', handleZoomStart)
    map.on('zoomend', handleZoomEnd)

    // Cleanup
    return () => {
      map.off('zoomstart', handleZoomStart)
      map.off('zoomend', handleZoomEnd)
    }
  }, [map, trackMapZoom])

  return null // This component doesn't render anything
}
